import {forwardRef, Ref, useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {translations} from '../../i18n/translations';
import {Box, Stack} from '@mui/material';
import {useForm, UseFormReturn} from 'react-hook-form';
import {FlatSelectDropdown} from '../flat-select-dropdown/flat-select-dropdown';
import {Option} from '../select-dropdown/select-dropdown';
import {ScheduleField} from '../schedule-field/schedule-field';
import {DEFAULT_FORM_VALIDATIONS} from '../../utils/constants';
import {FlatInputProps} from '../flat-input/flat-input';
import {PeriodicityForm, PeriodicityFormType} from '../periodicity-form/periodicity-form';
import {DynamicFormFieldShape} from '../dynamic-form';
import {Modal} from '../modal/modal';
import {ActivitiesGroupSchedule, SchedulerTypes} from '../../types';
import moment from 'moment';

export type ActivityGroupScheduleFormProps = FlatInputProps & {
  type: SchedulerTypes.ActivityGroup;
  open: boolean;
  onClose: () => void;
  updateDisplay: (data: any) => void;
  parentFormInstance: UseFormReturn<DynamicFormFieldShape>;
  startAtTimeId: string;
  startAtDateId: string;
};

export const ActivityGroupScheduleForm = forwardRef(
  (props: ActivityGroupScheduleFormProps, ref: Ref<HTMLInputElement>) => {
    const {t} = useTranslation();

    // trigger nested schedule modal
    const [openSchedule, setOpenSchedule] = useState(false);

    // enqueue react-hook-form
    const formInstance = useForm<PeriodicityFormType>({
      defaultValues: {
        startDate: moment().toISOString(),
        // @ts-ignore
        endDate: moment().toISOString(),
        // @ts-ignore
        endHour: moment().toISOString(),
      },
      ...DEFAULT_FORM_VALIDATIONS,
    });

    const options = [
      {
        key: 'activities-group',
        label: t(translations.periodicity_form.fields.frequency_value.activity),
      },
      {
        key: 'activity-type',
        label: t(translations.periodicity_form.fields.frequency_value.task),
      },
    ];

    const [selectedOption, setSelectedOption] = useState<Option>(options[0]);

    const [displayRepetition, setDisplayRepetition] = useState(false);

    const handleDropDownChange = (selectedOption: Option) => {
      if (selectedOption.key === 'activity-type')
        formInstance.unregister(['endDate', 'endHour', 'frequency', 'startDate', 'days']);

      setSelectedOption(selectedOption);
    };

    const handleDisplayPeriodicityForm = useCallback(
      () =>
        selectedOption.key === 'activities-group'
          ? setDisplayRepetition(true)
          : setDisplayRepetition(false),
      [selectedOption.key]
    );

    const onSubmit = () => {
      // get instance values
      const values = formInstance.getValues();

      let updatedData: ActivitiesGroupSchedule;

      if (selectedOption.key === 'activities-group') {
        // clean up
        updatedData = {
          ...values,
          // @ts-ignore
          frequency: values.frequency.key,
          frequencyValue: Number(values.frequencyValue),
          type: selectedOption.key,
        };
      } else {
        updatedData = {
          // @ts-ignore
          type: selectedOption.key,
        };
      }

      // update upstream form
      props.parentFormInstance.setValue(props.id ?? '', JSON.stringify(updatedData), {
        shouldDirty: true,
        shouldValidate: true,
      });

      // side-effect close
      handleModalClose();
    };

    const handleModalClose = () => {
      // reset form
      formInstance.reset();

      // close schedule options modal
      setOpenSchedule(false);
    };

    // outer parent form values for validations
    const [startAtDateValue, startAtTimeValue]: [
      startAtDateValue: string | undefined,
      startAtTimeValue: string | undefined,
    ] = props.parentFormInstance.getValues([props.startAtDateId, props.startAtTimeId]);

    useEffect(() => {
      // set default values based on incoming `activities-group-schedule` value
      if (props.value) {
        let values: ActivitiesGroupSchedule = JSON.parse(String(props.value));

        setSelectedOption({
          key: values.type,
          label: t(
            translations.periodicity_form.fields.frequency_value[
              values.type === 'activities-group' ? 'activity' : 'task'
            ]
          ),
        });

        let defaultValues: PeriodicityFormType = {
          ...values,
          // @ts-ignore
          endDate: values.endDate || moment(),
          // @ts-ignore
          endHour: values.endHour || moment(),
          // @ts-ignore
          startDate: values.startDate || moment(),
          // @ts-ignore
          frequency: values?.frequency
            ? {
                key: values.frequency,
                label: t(translations.periodicity_form.fields.frequency[values.frequency]),
              }
            : undefined,
          frequencyValue: values.frequencyValue ? Number(values.frequencyValue) : 1,
        };

        // set defaults
        formInstance.reset(defaultValues);
      }
    }, [formInstance, props.value, t]);

    useEffect(() => {
      handleDisplayPeriodicityForm();
    }, [handleDisplayPeriodicityForm, selectedOption]);

    // on opening field modal, evaluate repetition value
    useEffect(() => {
      if (openSchedule) {
        const scheduleValue = props.value;

        try {
          const parsedValue = JSON.parse(String(scheduleValue));

          // if value has key `type`, set the display toggle
          if (parsedValue.type) {
            setSelectedOption({
              key: parsedValue.type,
              label: t(
                translations.periodicity_form.fields.frequency_value[
                  parsedValue.type === 'activities-group' ? 'activity' : 'task'
                ]
              ),
            });
          }
        } catch (err) {
          // ignore if non-parseable
        }
      }
    }, [props.value, openSchedule, t]);

    return (
      <Box>
        <ScheduleField {...props} ref={ref} openSchedule={() => setOpenSchedule(true)} />
        <Modal
          title={t(translations.schedule_modal.activity.title)}
          actions={[
            {
              label: t(translations.modal.cancel),
              type: 'destructive',
              onClick: handleModalClose,
            },
            {
              label: t(translations.modal.save),
              type: 'neutral',
              disabled:
                selectedOption.key === 'activities-group' ? !formInstance.formState.isValid : false,
              onClick: onSubmit,
            },
          ]}
          open={openSchedule}
          PaperProps={{
            sx: {
              width: '397px',
            },
          }}
          keepMounted={false}
        >
          <Stack sx={{width: '100%', gap: '10px', display: 'flex'}}>
            <FlatSelectDropdown
              label={t(translations.periodicity_form.fields.frequency_value.label)}
              value={selectedOption}
              options={options}
              onChange={handleDropDownChange}
              required
              sx={{
                '.MuiAutocomplete-input': {
                  color: 'var(--dcm-gray)',
                },
                '.MuiFilledInput-input': {
                  fontWeight: 500,
                },
              }}
            />
            <PeriodicityForm
              type={SchedulerTypes.ActivityGroup}
              displayForm={displayRepetition}
              formInstance={formInstance}
              startAtDateValue={startAtDateValue}
              startAtTimeValue={startAtTimeValue}
            />
          </Stack>
        </Modal>
      </Box>
    );
  }
);
